import { useState, useEffect } from 'react';
import { casesApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
    Image, 
    Upload, 
    Trash2, 
    X
} from 'lucide-react';
import axios from 'axios';
import DragDropUpload from './DragDropUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

export default function CaseImagesManager({ caseId, caseData, onUpdate, isAdmin }) {
    const [uploading, setUploading] = useState(false);
    const [imageDescription, setImageDescription] = useState('');
    const [error, setError] = useState('');
    const [additionalImageUrls, setAdditionalImageUrls] = useState({});

    const token = localStorage.getItem('token');

    // Load additional images as blob URLs
    useEffect(() => {
        const loadImages = async () => {
            if (caseData.additional_images?.length > 0) {
                const urls = {};
                for (const img of caseData.additional_images) {
                    try {
                        const response = await axios.get(
                            `${API_URL}/cases/${caseId}/additional-images/${img.id}`,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                            }
                        );
                        urls[img.id] = URL.createObjectURL(response.data);
                    } catch (err) {
                        console.error('Error loading additional image:', err);
                    }
                }
                setAdditionalImageUrls(urls);
            } else {
                setAdditionalImageUrls({});
            }
        };

        loadImages();

        // Cleanup blob URLs on unmount
        return () => {
            Object.values(additionalImageUrls).forEach(url => URL.revokeObjectURL(url));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, caseData.additional_images, token]);

    // Gestisce upload da file input o drag and drop
    const handleUploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        
        // Salva posizione scroll
        const scrollPosition = window.scrollY;
        
        try {
            setUploading(true);
            setError('');
            for (const file of Array.from(files)) {
                await casesApi.uploadAdditionalImage(caseId, file, imageDescription);
            }
            setImageDescription('');
            await onUpdate();
            
            // Ripristina scroll (aumentato timeout per re-render completo)
            setTimeout(() => window.scrollTo(0, scrollPosition), 150);
        } catch (err) {
            setError('Errore nel caricamento delle immagini');
        } finally {
            setUploading(false);
        }
    };

    const handleUploadAdditionalImage = async (e) => {
        const files = e.target.files;
        handleUploadFiles(files);
    };

    const handleDeleteAdditionalImage = async (imageId) => {
        if (!window.confirm('Eliminare questa immagine?')) return;
        
        // Salva posizione scroll
        const scrollPosition = window.scrollY;
        
        try {
            await casesApi.deleteAdditionalImage(caseId, imageId);
            await onUpdate();
            
            // Ripristina scroll (aumentato timeout per re-render completo)
            setTimeout(() => window.scrollTo(0, scrollPosition), 150);
        } catch (err) {
            setError('Errore nell\'eliminazione dell\'immagine');
        }
    };

    return (
        <div>
            {error && (
                <div className="bg-red-500/10 border border-red-500 p-3 flex items-center justify-between mb-4">
                    <span className="text-red-500 text-sm">{error}</span>
                    <button onClick={() => setError('')}>
                        <X className="w-4 h-4 text-red-500" />
                    </button>
                </div>
            )}

            <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-[#00FF41]" />
                Immagini Aggiuntive
            </h3>

            {/* Upload Area con Drag & Drop - Solo Admin */}
            {isAdmin && (
                <div className="mb-4">
                    <DragDropUpload
                        onFilesSelected={handleUploadFiles}
                        onError={(msg) => setError(msg)}
                        accept="image/*,.pdf"
                        multiple={true}
                        disabled={uploading}
                        className="mb-3"
                    >
                        <div className="p-4 text-center">
                            <Upload className="w-6 h-6 mx-auto mb-2 text-white/40" />
                            <p className="text-white/60 text-sm">
                                Trascina qui le immagini o clicca per selezionare
                            </p>
                            <p className="text-white/30 text-xs mt-1">Supporta immagini e PDF</p>
                        </div>
                    </DragDropUpload>
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                        <div className="flex-1">
                            <Label className="text-white/60 uppercase text-xs">
                                Descrizione (opzionale)
                            </Label>
                            <Input
                                value={imageDescription}
                                onChange={(e) => setImageDescription(e.target.value)}
                                placeholder="Descrivi l'immagine..."
                                data-testid="additional-image-description-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                            />
                        </div>
                    </div>
                    {uploading && (
                        <div className="mt-2 flex items-center gap-2 text-[#00FF41]">
                            <div className="w-4 h-4 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                            <span className="text-sm">Caricamento in corso...</span>
                        </div>
                    )}
                </div>
            )}

            {/* Images Grid */}
            {(!caseData.additional_images || caseData.additional_images.length === 0) ? (
                <div className="text-center py-6 border border-dashed border-white/20">
                    <Image className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">Nessuna immagine</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    {caseData.additional_images.map((image) => (
                        <div
                            key={image.id}
                            data-testid={`additional-image-${image.id}`}
                            className="relative group bg-black border border-white/10 overflow-hidden"
                        >
                            {additionalImageUrls[image.id] ? (
                                <a 
                                    href={additionalImageUrls[image.id]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={additionalImageUrls[image.id]}
                                        alt={image.description || image.original_filename}
                                        className="w-full h-32 object-cover"
                                    />
                                </a>
                            ) : (
                                <div className="w-full h-32 flex items-center justify-center">
                                    <div className="w-6 h-6 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                                </div>
                            )}
                            <div className="p-2">
                                <p className="text-white/60 text-xs truncate">
                                    {image.original_filename}
                                </p>
                                {image.description && (
                                    <p className="text-white/40 text-xs truncate">
                                        {image.description}
                                    </p>
                                )}
                            </div>
                            {isAdmin && (
                                <button
                                    onClick={() => handleDeleteAdditionalImage(image.id)}
                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
